
import { supabase } from '../supabase';
import { buildCustomerList, CustomerListRow } from '../customerList';
import { fetchAll } from './fetchAll';
import { Newsletter, UserRole, User } from '../../types';
import { apiFetch } from '../apiFetch';

export const createNewsletter = async (n: Newsletter) => {
    const { error } = await supabase.from('newsletters').insert([{
        subject: n.subject,
        content: n.content,
        image_url: n.imageUrl,
        recipient_type: n.recipientType,
        recipient_group_id: n.recipientGroupId,
        sender_id: n.senderId,
        recipients: n.recipients,
        sent_at: new Date().toISOString(), // Or let default now() handle it
        expires_at: n.expiresAt ? new Date(n.expiresAt).toISOString() : null
    }]);
    if (error) throw error;
};

export const updateNewsletter = async (id: string, updates: Partial<Newsletter>) => {
    const dbPayload: any = {};
    
    // Map internal types (number) to DB columns (ISO string)
    if (updates.expiresAt !== undefined) {
        dbPayload.expires_at = updates.expiresAt ? new Date(updates.expiresAt).toISOString() : null;
    }
    if (updates.subject) dbPayload.subject = updates.subject;
    if (updates.content) dbPayload.content = updates.content;

    const { error } = await supabase.from('newsletters').update(dbPayload).eq('id', id);
    if (error) throw error;
};

export const sendNewsletterEmail = async (newsletter: Newsletter, recipientEmails: string[]) => {
    try {
        const response = await apiFetch('/api/send-newsletter', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                subject: newsletter.subject,
                content: newsletter.content,
                imageUrl: newsletter.imageUrl,
                recipients: recipientEmails
            })
        });

        let data;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
        } else {
            const text = await response.text();
            throw new Error(`Serverfel: API-rutten svarade inte med JSON (Status ${response.status}).`);
        }

        if (!response.ok) {
            throw new Error(data.error || `Serverfel: ${response.statusText}`);
        }

        return data;
    } catch (e: any) {
        console.error("Send Newsletter Error:", e);
        throw e;
    }
};

/**
 * Admin view: Fetch all history
 */
export const getAllNewsletters = async (): Promise<Newsletter[]> => {
    const { data, error } = await supabase
        .from('newsletters')
        .select('*')
        .order('sent_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(row => ({
        id: row.id,
        subject: row.subject,
        content: row.content,
        imageUrl: row.image_url,
        sentAt: new Date(row.sent_at).getTime(),
        expiresAt: row.expires_at ? new Date(row.expires_at).getTime() : undefined,
        recipientType: row.recipient_type,
        recipientGroupId: row.recipient_group_id,
        senderId: row.sender_id,
        recipients: row.recipients
    }));
};

/**
 * User view: Get latest relevant and ACTIVE newsletter
 */
export const getLatestNewsletter = async (user: User): Promise<Newsletter | null> => {
    if (user.role === UserRole.ADMIN) return null;

    const now = new Date().toISOString(); // Use ISO string
    
    // Build query with filters
    let query = supabase.from('newsletters')
        .select('*')
        // Only show if not expired OR if expires_at is null (eternal)
        .or(`expires_at.is.null,expires_at.gt.${now}`)
        .order('sent_at', { ascending: false });

    // Filter by audience
    const userGroupId = user.role === UserRole.TEACHER ? user.assignedClassId : user.classId;

    if (user.role === UserRole.STUDENT) {
        // Students see ALL or their specific GROUP
        let filter = "recipient_type.eq.ALL";
        if (userGroupId) filter += `,recipient_group_id.eq.${userGroupId}`;
        query = query.or(filter);
    } else if (user.role === UserRole.TEACHER) {
        // Teachers see ALL, ROLE (Teachers) or their specific GROUP
        let filter = "recipient_type.in.(ALL,ROLE)";
        if (userGroupId) filter += `,recipient_group_id.eq.${userGroupId}`;
        query = query.or(filter);
    }

    const { data, error } = await query.limit(1).maybeSingle();
    
    if (error) return null;
    if (!data) return null;

    return {
        id: data.id,
        subject: data.subject,
        content: data.content,
        imageUrl: data.image_url,
        sentAt: new Date(data.sent_at).getTime(),
        expiresAt: data.expires_at ? new Date(data.expires_at).getTime() : undefined,
        recipientType: data.recipient_type,
        recipientGroupId: data.recipient_group_id,
        senderId: data.sender_id,
        recipients: data.recipients
    };
};

/** Alla som handlat, en rad per e-post, med reklamval och avregistrering. */
export const getCustomerList = async (): Promise<CustomerListRow[]> => {
    // Sida för sida: fler än 1 000 ordrar eller avregistreringar annars tappas tyst.
    const [orders, suppressed] = await Promise.all([
        fetchAll<any>((from, to) => supabase
            .from('orders')
            .select('id, customer_email, customer_name, marketing_consent, created_at, student_name_snapshot')
            .is('deleted_at', null)
            .not('customer_email', 'is', null)
            .order('id')
            .range(from, to)),
        fetchAll<any>((from, to) => supabase.from('newsletter_suppressions').select('email, source').order('email').range(from, to)),
    ]);
    return buildCustomerList(
        orders.map((o: any) => ({
            email: String(o.customer_email),
            name: String(o.customer_name || ''),
            marketingConsent: o.marketing_consent === true,
            createdAt: new Date(o.created_at).getTime(),
            sellerName: o.student_name_snapshot || undefined,
        })),
        suppressed.map((s: any) => ({ email: String(s.email), source: s.source })),
    );
};

/**
 * Kunder som får reklam: kundens senaste köp avgör och avregistrerade tas
 * bort — samma regel som kundlistan, så att listan och utskicket stämmer.
 */
export const getOptInCustomers = async (): Promise<string[]> =>
    (await getCustomerList()).filter(c => c.marketing).map(c => c.email);

/**
 * Unsubscribe functionality
 * Calls the secure RPC function 'unsubscribe_guest'
 */
export const unsubscribeGuest = async (email: string, reason?: string) => {
    const { error } = await supabase.rpc('unsubscribe_guest', { 
        email_input: email, 
        reason_input: reason || null 
    });
    
    if (error) throw error;
};
